// Rankaro Subscription Plans - 2026 Restructure

export type PlanType = 'free' | 'starter' | 'pro' | 'scale' | 'agency';

export interface PlanFeatures {
  seo_title: boolean;
  short_description: boolean;
  long_description: boolean;
  meta_title: boolean;
  meta_description: boolean;
  alt_text: boolean;
  categories_seo: boolean;
  bulk_processing: boolean;
  auto_processing: boolean;
  regeneration: boolean;
  history: boolean;
  smart_review: boolean;
  api_access: boolean;
  priority_support: boolean;
  keyword_research: boolean;
}

export interface Plan {
  id: string;
  name: string;
  planType: PlanType;
  priceMonthly: number;
  priceAnnual: number; // Annual price (old monthly x 12)
  creditsMonthly: number; // Unified credits (products + categories)
  categoryLimitMonthly: number | null; // null = unlimited
  storeLimit: number; // Max stores allowed
  features: PlanFeatures;
  description: string;
  forWho: string; // Target audience description
  cta: string;
  popular?: boolean;
  badge?: string; // "Mejor valor", "Más popular", etc.
}

// Static plan definitions for UI (matches DB)
export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    planType: 'free',
    priceMonthly: 0,
    priceAnnual: 0,
    creditsMonthly: 15,
    categoryLimitMonthly: 5,
    storeLimit: 1,
    description: 'Para probar sin compromiso',
    forWho: 'Ideal para tiendas pequeñas que quieren probar',
    cta: 'Comenzar gratis',
    features: {
      seo_title: true,
      short_description: true,
      long_description: false,
      meta_title: true,
      meta_description: true,
      alt_text: true,
      categories_seo: true,
      bulk_processing: false,
      auto_processing: false,
      regeneration: false,
      history: false,
      smart_review: false,
      api_access: false,
      priority_support: false,
      keyword_research: false,
    },
  },
  {
    id: 'starter',
    name: 'Starter',
    planType: 'starter',
    priceMonthly: 20,
    priceAnnual: 180,
    creditsMonthly: 150,
    categoryLimitMonthly: 30,
    storeLimit: 1,
    description: 'Para tiendas que empiezan a crecer',
    forWho: 'Tiendas pequeñas y medianas, mercados LATAM',
    cta: 'Comenzar con Starter',
    badge: 'Mejor valor',
    features: {
      seo_title: true,
      short_description: true,
      long_description: true,
      meta_title: true,
      meta_description: true,
      alt_text: true,
      categories_seo: true,
      bulk_processing: true,
      auto_processing: false,
      regeneration: true,
      history: true,
      smart_review: false,
      api_access: false,
      priority_support: false,
      keyword_research: false,
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    planType: 'pro',
    priceMonthly: 53,
    priceAnnual: 468,
    creditsMonthly: 600,
    categoryLimitMonthly: 100,
    storeLimit: 2,
    description: 'Para tiendas que quieren resultados reales',
    forWho: 'Una tienda de 600 productos optimizada en una tarde',
    cta: 'Comenzar con Pro',
    popular: true,
    features: {
      seo_title: true,
      short_description: true,
      long_description: true,
      meta_title: true,
      meta_description: true,
      alt_text: true,
      categories_seo: true,
      bulk_processing: true,
      auto_processing: true,
      regeneration: true,
      history: true,
      smart_review: false,
      api_access: false,
      priority_support: false,
      keyword_research: true,
    },
  },
  {
    id: 'scale',
    name: 'Scale',
    planType: 'scale',
    priceMonthly: 134,
    priceAnnual: 1188,
    creditsMonthly: 2500,
    categoryLimitMonthly: null, // unlimited
    storeLimit: 5,
    description: 'Para catálogos grandes y equipos',
    forWho: 'Procesamiento automático cuando añades productos',
    cta: 'Escalar ahora',
    features: {
      seo_title: true,
      short_description: true,
      long_description: true,
      meta_title: true,
      meta_description: true,
      alt_text: true,
      categories_seo: true,
      bulk_processing: true,
      auto_processing: true,
      regeneration: true,
      history: true,
      smart_review: true,
      api_access: true,
      priority_support: false,
      keyword_research: true,
    },
  },
  {
    id: 'agency',
    name: 'Agency',
    planType: 'agency',
    priceMonthly: 269,
    priceAnnual: 2388,
    creditsMonthly: 5000,
    categoryLimitMonthly: null, // unlimited
    storeLimit: 25,
    description: 'Para agencias que gestionan múltiples clientes',
    forWho: 'Gestiona hasta 25 tiendas con un solo plan',
    cta: 'Escalar como agencia',
    features: {
      seo_title: true,
      short_description: true,
      long_description: true,
      meta_title: true,
      meta_description: true,
      alt_text: true,
      categories_seo: true,
      bulk_processing: true,
      auto_processing: true,
      regeneration: true,
      history: true,
      smart_review: true,
      api_access: true,
      priority_support: true,
      keyword_research: true,
    },
  },
];

export const getPlanByType = (type: PlanType): Plan => {
  return PLANS.find(p => p.planType === type) || PLANS[0];
};

export const getPlanDisplayName = (type: PlanType): string => {
  const names: Record<PlanType, string> = {
    free: 'Plan Free',
    starter: 'Plan Starter',
    pro: 'Plan Pro',
    scale: 'Plan Scale',
    agency: 'Plan Agency',
  };
  return names[type];
};

// Map a database row to the Plan type used by the frontend
export const mapDbRowToPlan = (row: any): Plan => {
  const features = (row.features || {}) as Record<string, boolean>;
  return {
    id: row.id,
    name: row.name,
    planType: row.plan_type as PlanType,
    priceMonthly: Number(row.price_monthly) || 0,
    priceAnnual: Number(row.price_annual) || 0,
    creditsMonthly: row.product_limit_monthly || 0,
    categoryLimitMonthly: row.category_limit_monthly ?? null,
    storeLimit: row.store_limit || 1,
    features: {
      seo_title: features.seo_title ?? true,
      short_description: features.short_description ?? true,
      long_description: features.long_description ?? false,
      meta_title: features.meta_title ?? true,
      meta_description: features.meta_description ?? true,
      alt_text: features.alt_text ?? true,
      categories_seo: features.categories_seo ?? false,
      bulk_processing: features.bulk_processing ?? false,
      auto_processing: features.auto_processing ?? false,
      regeneration: features.regeneration ?? false,
      history: features.history ?? false,
      smart_review: features.smart_review ?? false,
      api_access: features.api_access ?? false,
      priority_support: features.priority_support ?? false,
      keyword_research: features.keyword_research ?? false,
    },
    description: row.description || '',
    forWho: row.for_who || '',
    cta: row.cta || '',
    popular: row.popular || false,
    badge: row.badge || undefined,
  };
};

// Backwards compatibility - map to creditsMonthly
export const getProductLimitMonthly = (type: PlanType): number => {
  const plan = getPlanByType(type);
  return plan.creditsMonthly;
};

export const getCategoryLimitDisplay = (limit: number | null): string => {
  if (limit === null) return 'Ilimitadas';
  if (limit === 0) return 'No incluidas';
  return `Hasta ${limit}`;
};

export const getStoreLimitDisplay = (limit: number): string => {
  if (limit === 1) return '1 tienda';
  return `Hasta ${limit} tiendas`;
};

// Trial helpers
export const isTrialActive = (trialEndsAt: string | null): boolean => {
  if (!trialEndsAt) return false;
  return new Date(trialEndsAt) > new Date();
};

export const getTrialDaysRemaining = (trialEndsAt: string | null): number => {
  if (!trialEndsAt) return 0;
  const diff = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

// Feature display helpers
export interface FeatureDisplay {
  label: string;
  included: boolean;
  highlight?: boolean;
}

export const getPlanFeaturesForDisplay = (plan: Plan): FeatureDisplay[] => {
  const features: FeatureDisplay[] = [];
  
  // Credits limit (unified)
  features.push({
    label: `Hasta ${plan.creditsMonthly} créditos/mes`,
    included: true,
  });
  
  // Store limit
  if (plan.storeLimit > 1) {
    features.push({
      label: `Hasta ${plan.storeLimit} tiendas`,
      included: true,
      highlight: true,
    });
  } else {
    features.push({
      label: '1 tienda',
      included: true,
    });
  }
  
  // Categories
  features.push({
    label: plan.categoryLimitMonthly === null 
      ? 'Categorías ilimitadas' 
      : plan.categoryLimitMonthly === 0 
        ? 'Categorías no incluidas'
        : `Hasta ${plan.categoryLimitMonthly} categorías`,
    included: plan.categoryLimitMonthly !== 0,
  });
  
  // SEO básico
  features.push({
    label: 'Título SEO + Meta tags',
    included: true,
  });
  
  features.push({
    label: 'Descripción corta + ALT text',
    included: true,
  });
  
  // Long description
  features.push({
    label: 'Descripción larga SEO',
    included: plan.features.long_description,
    highlight: plan.features.long_description,
  });
  
  // Bulk
  features.push({
    label: 'Procesamiento masivo (bulk)',
    included: plan.features.bulk_processing,
    highlight: plan.features.bulk_processing,
  });
  
  // Regeneration
  if (plan.features.regeneration) {
    features.push({
      label: 'Re-generación de contenido',
      included: true,
    });
  }
  
  // History
  if (plan.features.history) {
    features.push({
      label: 'Historial de generaciones',
      included: true,
    });
  }
  
  // Detección automática de idioma - incluido en todos los planes
  features.push({
    label: 'Detección automática de idioma',
    included: true,
  });
  
  // Auto processing
  if (plan.features.auto_processing) {
    features.push({
      label: 'Procesamiento automático',
      included: true,
      highlight: true,
    });
  }
  
  // Smart review
  if (plan.features.smart_review) {
    features.push({
      label: 'Revisión inteligente de contenido',
      included: true,
      highlight: true,
    });
  }
  
  // API Access
  if (plan.features.api_access) {
    features.push({
      label: 'Acceso API',
      included: true,
      highlight: true,
    });
  }
  
  // Priority support
  if (plan.features.priority_support) {
    features.push({
      label: 'Soporte prioritario',
      included: true,
      highlight: true,
    });
  }

  // Keyword Research (Pro+) with descriptive limits
  const kwLabel = !plan.features.keyword_research
    ? 'Investigación de keywords con IA'
    : plan.planType === 'pro'
      ? 'Keywords con IA (3 análisis + 50 validaciones/mes)'
      : plan.planType === 'scale'
        ? 'Keywords con IA (análisis ilimitados + 200 validaciones/mes)'
        : 'Keywords con IA (ilimitado)';

  features.push({
    label: kwLabel,
    included: plan.features.keyword_research,
    highlight: plan.features.keyword_research,
  });

  return features;
};
